/**
 * To buy (BLU-1268, plan 90 section 5.5): the reads and writes behind the
 * Purchase orders "To buy" tab, one module so the tab stays presentational and
 * its test mocks one thing.
 *
 *   useBuyToday       open just-in-time POs at the location: the EXISTING PO
 *                     list query with `kind` + `status`, filtered, counted and
 *                     paged in the same SQL (filter-layer rule).
 *   useToBuyList      stocked blanks below keep at least (ONE get_to_buy RPC).
 *                     Owner of the floor only: anyone else gets FORBIDDEN,
 *                     which the tab shows as a permission state, never an error.
 *   useCompleteAsListed  Bought as listed from a Buy today card (the shared
 *                     useCompleteJustInTime, also behind the JIT PO page).
 *   useMakeDrafts     createDraftsFromToBuy for one supplier's lines.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CreateDraftsFromToBuyResult } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import { jitPoErrorText } from '../lib/jit-po-error-text';
import { useCompleteJustInTime, type CompletableJustInTimeOrder } from './useCompleteJustInTime';

/** Open just-in-time POs listed at once; more than this links to the filtered list. */
export const BUY_TODAY_LIMIT = 50;

export function useBuyToday(locationId: string | null) {
  const query = trpc.purchaseOrders.getOrders.useQuery(
    {
      locationId: locationId ?? undefined,
      orderType: 'purchase',
      kind: 'just_in_time',
      status: 'submitted',
      sortBy: 'created_at',
      sortOrder: 'asc',
      limit: BUY_TODAY_LIMIT,
      offset: 0,
    },
    { enabled: Boolean(locationId), staleTime: 30_000 },
  );
  return {
    orders: query.data?.data ?? [],
    total: query.data?.pagination.total ?? 0,
    loading: query.isLoading,
    failed: Boolean(query.error) && !query.data,
    retrying: query.isFetching,
    retry: () => void query.refetch(),
  };
}

export function useToBuyList(locationId: string | null) {
  const query = trpc.purchaseOrders.getToBuy.useQuery(
    { locationId: locationId ?? '' },
    {
      enabled: Boolean(locationId),
      staleTime: 30_000,
      // A refusal is an answer, not a blip: never retry FORBIDDEN.
      retry: (count, error) => trpcErrorCode(error) !== 'FORBIDDEN' && count < 2,
    },
  );
  const forbidden = trpcErrorCode(query.error) === 'FORBIDDEN';
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    forbidden,
    failed: Boolean(query.error) && !query.data && !forbidden,
    retrying: query.isFetching,
    retry: () => void query.refetch(),
  };
}

export function useCompleteAsListed() {
  const { complete, pendingOrderId } = useCompleteJustInTime();
  const completeAsListed = (order: CompletableJustInTimeOrder): Promise<boolean> => complete(order, true);
  return { completeAsListed, pendingOrderId };
}

export function useMakeDrafts() {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [pendingSupplierKey, setPendingSupplierKey] = useState<string | null>(null);
  const mutation = trpc.purchaseOrders.createDraftsFromToBuy.useMutation({
    onSuccess: () =>
      Promise.all([utils.purchaseOrders.getToBuy.invalidate(), utils.purchaseOrders.getOrders.invalidate()]),
  });

  const makeDrafts = async (args: {
    locationId: string;
    supplierKey: string;
    supplierName: string;
    lines: Array<{ skuId: string; qty: number }>;
  }): Promise<CreateDraftsFromToBuyResult | null> => {
    setPendingSupplierKey(args.supplierKey);
    try {
      const result = await mutation.mutateAsync({ locationId: args.locationId, lines: args.lines });
      const first = result.created[0];
      if (!first) {
        toast({
          title: t('toBuy.toast.nothingNew', { supplier: args.supplierName }),
          description: t('toBuy.toast.nothingNewBody'),
        });
      } else {
        toast({
          title: t('toBuy.toast.made', { po: first.poNumber, supplier: first.supplierName ?? args.supplierName }),
          description: t('toBuy.toast.madeBody', { count: first.lines }),
          action: (
            <ToastAction altText={t('toBuy.toast.open')} onClick={() => navigate(`/orders/${first.id}`)}>
              {t('toBuy.toast.open')}
            </ToastAction>
          ),
        });
      }
      return result;
    } catch (error) {
      toast({ title: t('toBuy.toast.failed'), description: jitPoErrorText(t, error), variant: 'destructive' });
      return null;
    } finally {
      setPendingSupplierKey(null);
    }
  };

  return { makeDrafts, pendingSupplierKey };
}
